import axios from 'axios'
import Resizer from 'utils/imageResizer'

export async function uploadCommentBodyToS3(
  htmlContent: string,
  getPresignedUrl: (payload: any) => Promise<any>
): Promise<{ s3Path: string; s3Url: string }> {
  if (!htmlContent) return { s3Path: '', s3Url: '' }

  try {
    const resizedHtml = await Resizer.ResizedQuillImages(htmlContent)
    const blob = new Blob([resizedHtml], { type: 'text/plain' })
    const file = new File([blob], 'comment-body.txt', { type: 'text/plain' })

    const data = await getPresignedUrl({
      data: {
        extension: 'txt',
        fileName: 'comment-body'
      }
    })

    const { key, presignedUrl } =
      data?.data?.s3PreSignedLinks?.[0] || data?.data?.s3PreSignLinks?.[0] || {}

    if (key && presignedUrl) {
      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': 'text/plain' }
      })
      return { s3Path: key, s3Url: presignedUrl }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to upload comment body to S3:', err)
  }

  return { s3Path: '', s3Url: htmlContent }
}

export function isS3Url(urlOrText?: string | null): boolean {
  if (!urlOrText || typeof urlOrText !== 'string') return false
  const trimmed = urlOrText.trim()
  return trimmed.startsWith('http://') || trimmed.startsWith('https://')
}

export function isS3Path(pathOrText?: string | null): boolean {
  if (!pathOrText || typeof pathOrText !== 'string') return false
  const trimmed = pathOrText.trim()
  return (
    trimmed.startsWith('assets/') ||
    trimmed.includes('/comment-body/') ||
    trimmed.includes('comment-body/')
  )
}

export async function fetchCommentBodyContent(
  bodyOrPath?: string | null
): Promise<string> {
  if (!bodyOrPath) return ''
  const trimmed = bodyOrPath.trim()
  if (isS3Url(trimmed)) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)

      const response = await fetch(trimmed, { signal: controller.signal })
      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`Failed to fetch S3 content: ${response.status}`)
      }
      return await response.text()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching S3 comment body:', err)
      throw err
    }
  }

  // If bodyOrPath is an unresolved S3 storage key without an HTTP URL, do not return it as plain text
  if (isS3Path(trimmed)) {
    if (typeof window !== 'undefined' && (window as any).rnCommentsMap) {
      const gMap: any = (window as any).rnCommentsMap
      if (gMap instanceof Map) {
        const found = Array.from(gMap.values()).find((item: any) => {
          const s3 = item?.s3Path || item?.s3_path
          return (
            s3 &&
            isS3Url(s3) &&
            (item?.body === trimmed || s3.includes(trimmed))
          )
        })
        if (found) {
          const s3 = (found as any).s3Path || (found as any).s3_path
          return fetchCommentBodyContent(s3)
        }
      }
    }
    throw new Error(`Unresolved S3 path: ${trimmed}`)
  }

  return trimmed
}
